import type { ReportPeriod } from '@eriko/shared-types';
import { cn } from '../lib/utils';
import { CustomRangePopover } from './CustomRangePopover';
import type { CustomRange } from './types';

interface ReportsPeriodTabsProps {
  period: ReportPeriod;
  setPeriod: (next: ReportPeriod) => void;
  visiblePeriods: ReportPeriod[];
  periodLabels: Record<ReportPeriod, string>;
  periodLabel: string;
  customRange: CustomRange | null;
  applyCustomRange: (range: CustomRange) => void;
  refreshing: boolean;
}

export function ReportsPeriodTabs({
  period,
  setPeriod,
  visiblePeriods,
  periodLabels,
  periodLabel,
  customRange,
  applyCustomRange,
  refreshing,
}: ReportsPeriodTabsProps) {
  return (
    <div className="flex items-center justify-between border-b border-surface-300/70 mb-10">
      <div role="tablist" aria-label="Report period" className="flex items-stretch gap-7">
        {visiblePeriods.map((p) => {
          const selected = period === p;
          if (p === 'custom') {
            return (
              <CustomRangePopover
                key={p}
                selected={selected}
                currentRange={customRange}
                onApply={applyCustomRange}
              />
            );
          }
          return (
            <button
              key={p}
              role="tab"
              type="button"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => setPeriod(p)}
              className={cn(
                'relative py-3 text-[13px] font-medium transition-colors cursor-pointer',
                'focus-visible:outline-none focus-visible:text-surface-900',
                selected
                  ? 'text-surface-900'
                  : 'text-surface-500 hover:text-surface-900',
              )}
            >
              {periodLabels[p]}
              {selected && (
                <span
                  aria-hidden
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-7 rounded-full bg-neg"
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        {refreshing && (
          <span
            role="status"
            aria-live="polite"
            className="text-[11px] font-medium uppercase tracking-[1.5px] text-surface-600"
          >
            Updating…
          </span>
        )}
        <span className="font-mono text-[11px] text-surface-500 flex items-center gap-2">
          <span className="inline-block w-1 h-1 rounded-full bg-neg" />
          {customRange ? `${customRange.from} — ${customRange.to}` : periodLabel}
        </span>
      </div>
    </div>
  );
}
